import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, Film, Loader2, PlayCircle } from "lucide-react";
import AppLayout from "@/components/AppLayout";
import BilingualText from "@/components/BilingualText";
import Mascot from "@/components/Mascot";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function Lessons() {
  const { currentProfile } = useAuth();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/lessons").then(({ data }) => setLessons(data || [])).finally(() => setLoading(false));
  }, []);

  const level = currentProfile?.level;
  const forYou = lessons.filter((l) => !level || l.level === level);
  const others = lessons.filter((l) => level && l.level !== level);

  return (
    <AppLayout>
      <div data-testid="lessons-root" className="space-y-10">
        <div className="flex items-center gap-4">
          <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-100 to-amber-100 grid place-items-center">
            <Film className="w-5 h-5 text-emerald-700" />
          </span>
          <div>
            <BilingualText cn="视频课程" en="Video lessons" size="hero" />
            <p className="mt-1 text-sm text-slate-500">
              <span className="cn mr-1">由{`小甲教授`}讲课，中英双语字幕 · </span>
              Narrated by Prof. Scaly with bilingual captions.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            {forYou.length > 0 && (
              <LessonSection
                title_cn="为你推荐"
                title_en="Made for you"
                lessons={forYou}
              />
            )}
            {others.length > 0 && (
              <LessonSection
                title_cn="更多课程"
                title_en="More lessons"
                lessons={others}
              />
            )}
            {!lessons.length && (
              <div className="card-plush p-10 text-center text-slate-400">
                No lessons yet. Check back soon!
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}

function LessonSection({ title_cn, title_en, lessons }) {
  return (
    <section>
      <div className="flex items-baseline justify-between mb-5">
        <BilingualText cn={title_cn} en={title_en} size="md" />
        <span className="text-xs font-bold text-slate-400">
          {lessons.length} {lessons.length === 1 ? "lesson" : "lessons"}
        </span>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        {lessons.map((l, i) => (
          <LessonCard key={l.slug} lesson={l} index={i} />
        ))}
      </div>
    </section>
  );
}

function LessonCard({ lesson, index }) {
  const minutes = Math.max(1, Math.round((lesson.duration_est || 240) / 60));
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04, duration: 0.35 }}
      whileHover={{ y: -3 }}
    >
      <Link
        to={`/lessons/${lesson.slug}`}
        data-testid={`lesson-card-${lesson.slug}`}
        className="block card-plush p-5 sm:p-6 hover:shadow-md transition-shadow group"
      >
        <div className="flex items-start gap-4">
          <div className="relative shrink-0">
            <Mascot size="sm" ring={false} className="border-2 border-white shadow" />
            <span className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-slate-900 text-white grid place-items-center shadow-lg group-hover:scale-110 transition-transform">
              <PlayCircle className="w-5 h-5" />
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="badge-soft">{lesson.level}</span>
              {lesson.grade_hint && (
                <span className="badge-soft bg-emerald-50 text-emerald-700">{lesson.grade_hint}</span>
              )}
              <span className="text-xs text-slate-400 font-bold inline-flex items-center gap-1">
                <Clock className="w-3 h-3" />
                ~{minutes} min · {lesson.slide_count} slides
              </span>
            </div>
            <BilingualText cn={lesson.title_cn} en={lesson.title_en} size="md" />
            <p className="mt-2 text-sm text-slate-500 leading-snug line-clamp-3">
              <span className="cn block text-slate-700">{lesson.description_cn}</span>
              {lesson.description_en}
            </p>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
